/* eslint-disable */
import React from "react";
import {
  Box,
  Card,
  Grid,
  Typography,
  useTheme,
  Chip,
  Stack,
  Tooltip,
  Divider,
} from "@mui/material";
import { motion } from "framer-motion";
import SearchIcon from "@mui/icons-material/Search";
import EditNoteIcon from "@mui/icons-material/EditNote";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SeoContentPlanner from "../../Images/seocontentplanner.svg";
import BlogWriter from "../../Images/blog-writer.svg";
import BlogGeneration from "../../Images/blog-generation.svg";
import KeywordTracking from "../../Images/keyword-tracking.svg";
import { Link } from "react-router-dom";

const MotionBox = motion(Box);

function ExploreFeature() {
  const theme = useTheme();

  const features = [
    {
      title: "AI Blog Generator",
      image: BlogGeneration,
      description:
        "Create structured, keyword-driven blogs with step-by-step AI guidance.",
      link: "/ai-blog-writer",
      // badge: "Recommended",
      tag: "Step-by-step AI",
      tooltip: "Guided AI workflow from outline to final blog",
      // highlight: true,
    },
    {
      title: "SEO Content Writer",
      image: BlogWriter,
      description:
        "Write and optimize content while comparing it against top-ranking competitors in real time.",
      link: "/content-score",
      // link: "/seoContentWriter",
      tag: "Real-time SEO",
      tooltip: "Live SEO scoring and competitor comparison",
    },
    {
      title: "SEO Content Planner",
      image: SeoContentPlanner,
      description:
        "Analyze URLs with geolocation, find high-value keywords, and plan SEO-ready blogs.",
      link: "/keywordsDashboard",
      tag: "Content Strategy",
      tooltip: "Keyword clustering and content planning",
    },
    {
      title: "Keyword Research",
      image: KeywordTracking,
      description:
        "Discover high-value keywords with actionable insights to improve visibility and drive traffic.",
      link: "/keyword-reserch?menu=headerKeywordReserch",
      tag: "Keyword Discovery",
      tooltip: "Find high-volume, low-competition & trending keywords with competitor and local insights",
    }

  ];

  const workflow = [
    { label: "Research", icon: SearchIcon },
    { label: "Plan", icon: EditNoteIcon },
    { label: "Generate", icon: AutoAwesomeIcon },
    { label: "Optimize", icon: EditNoteIcon },
    { label: "Track", icon: TrendingUpIcon },
  ];

  return (
    <Box className="exploreFeature">
      <Card
        sx={{
          padding: "40px 28px",
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "none",
          borderRadius: 3,
        }}
      >
        {/* Header */}
        <Box textAlign="center" mb={4}>
          <Typography sx={{ fontSize: 24, fontWeight: 800, mb: 1 }}>
            AI Content Generator Hub
          </Typography>
          <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
            Research → Plan → Generate → Optimize → Track
          </Typography>
        </Box>

        {/* Workflow Row */}
        <Stack
          direction="row"
          spacing={3}
          justifyContent="center"
          mb={4}
          flexWrap="wrap"
        >
          {workflow.map((step, i) => {
            const Icon = step.icon;
            return (
              <MotionBox
                key={step.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  fontSize: 13,
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                }}
              >
                <Icon sx={{ fontSize: 18 }} />
                {step.label}
              </MotionBox>
            );
          })}
        </Stack>

        <Divider sx={{ mb: 4 }} />

        {/* Feature Cards */}
        <Grid container spacing={3}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Link to={feature.link} style={{ textDecoration: "none" }}>
                <MotionBox
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -6 }}
                  sx={{
                    display: "flex",
                    gap: 2,
                    padding: 3,
                    borderRadius: 3,
                    background: feature.highlight
                      ? "linear-gradient(135deg, #eef4ff, #f8fbff)"
                      : theme.palette.background.paper,
                    border: feature.highlight
                      ? `1px solid ${theme.palette.primary.main}`
                      : `1px solid ${theme.palette.divider}`,
                    transition: "all 0.3s ease",
                    cursor: "pointer",

                    "&:hover": {
                      boxShadow: "0 16px 36px rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  {/* Icon */}
                  <Box
                    component="img"
                    src={feature.image}
                    alt={feature.title}
                    sx={{
                      width: 56,
                      height: 56,
                      objectFit: "contain",
                      flexShrink: 0,
                    }}
                  />

                  {/* Content */}
                  <Box flex={1}>
                    <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                      <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                        {feature.title}
                      </Typography>

                      {feature.badge && (
                        <Chip label={feature.badge} size="small" color="primary" />
                      )}
                    </Stack>

                    <Typography
                      sx={{
                        fontSize: 13,
                        color: "text.secondary",
                        mb: 1.2,
                      }}
                    >
                      {feature.description}
                    </Typography>

                    <Stack direction="row" spacing={1} alignItems="center">
                      <Tooltip title={feature.tooltip}>
                        <Chip
                          label={feature.tag}
                          size="small"
                          variant="outlined"
                        />
                      </Tooltip>

                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: theme.palette.primary.main,
                        }}
                      >
                        Open tool →
                      </Typography>
                    </Stack>
                  </Box>
                </MotionBox>
              </Link>
            </Grid>
          ))}
        </Grid>
      </Card>
    </Box>
  );
}

export default ExploreFeature;
